<script lang="ts">
  import { useLocation, useNavigate } from 'svelte-navigator';
  import Page from '../components/misc/Page.svelte';
  import Tabs from '../components/misc/Tabs.svelte';
  import Info from '../components/misc/InfoBox.svelte';
  import { AppState } from '../etc/stores';
  import { cdn, formatMoney } from '../etc/utils';
  import { _ } from 'svelte-i18n';
  import HeaderInternal from '../components/misc/HeaderInternal.svelte';
  import { onDestroy, onMount } from 'svelte';
  import Analytics from '../services/analytics';
  import { replaceVariables } from '../i18n';
  import Dashed from '../components/icons/Dashed.svelte';
  import { fade } from 'svelte/transition';

  const location = useLocation();
  const navigate = useNavigate();

  const onBackButton = $location.state.onBackButton;
  const navAnimation: boolean = $location.state?.navAnimation;

  let startAnimation = false;
  let handleNavigationBackPage;
  let timeoutAnimation;

  const t = (text: string, values?: any) => {
    return replaceVariables($_(`how_it_works.${text}`), values);
  };

  const tUsing = (text: string) => {
    return t(`tab_using.${text}`);
  };

  const tEarning = (text: string, values?: any) => {
    return t(`tab_earning.${text}`, values);
  };

  const tabs = [
    {
      label: tEarning('tab_title'),
      value: 'earning'
    },
    {
      label: tUsing('tab_title'),
      value: 'using'
    }
  ];

  let selectedTab = tabs[0].value;

  const changeTab = (newValue: string) => {
    selectedTab = newValue;
  };

  const onClickBack = () => {
    if (onBackButton) onBackButton();
    else {
      if (navAnimation) {
        clearTimeout(timeoutAnimation);
        startAnimation = true;
        timeoutAnimation = setTimeout(() => {
          if (handleNavigationBackPage) handleNavigationBackPage();
          navigate(-1);
        }, 600);
      } else {
        navigate(-1);
      }
    }
  };

  onMount(() => {
    Analytics.logPageView('How it Works', '/how-it-works');
  });

  onDestroy(() => {
    clearTimeout(timeoutAnimation);
  });
</script>

<HeaderInternal
  onBack={onClickBack}
  title={t('title')}
  {navAnimation}
  {startAnimation}
  bind:handleNavigationBackPage
/>
<Page padding="0" hideCloseButton headerInternal>
  <div
    class="twism-tab-wrapper"
    class:--animation={startAnimation}
    in:fade={{ duration: navAnimation ? 200 : 0 }}
  >
    <Tabs {tabs} onChange={changeTab} />
    {#if selectedTab === 'earning'}
      <div class="tab tab-content-box earning">
        {#if $AppState.business.loyaltyEnabled}
          <Info
            title={t('loyalty_title', {
              LOYALTY: formatMoney(
                $AppState.business.location,
                100 /
                  Number(
                    $AppState.business.rewardsSettings.coinRewardPercentage
                  )
              )
            })}
            description={t('loyalty_description')}
          />
        {/if}
        <div class="title">
          <h2>{tEarning('title')}</h2>
          <i
            class="tab-icon"
            style={`background-image: url('${process.env.BASE_URL}/images/earning-coins.svg');`}
          />
        </div>
        <p>
          {tEarning('coin', {
            currency: formatMoney($AppState.business.location, 1)
          })}
        </p>
        <Dashed class="twism-how-it-works-divider" />
        <h2>{tEarning('ways_earn_coin.title')}</h2>
        <ol>
          <li>
            <p>
              {tEarning('ways_earn_coin.step-1')}
            </p>
          </li>
          <li>
            <p>{tEarning('ways_earn_coin.step-2')}</p>
          </li>
        </ol>
        <div class="store-badge-container">
          <a
            href="https://play.google.com/store/apps/details?id=com.twism"
            target="_blank"
            class="store-badge"
          >
            <img src={cdn('/images/google-play-badge.svg')} alt="Google Play" />
          </a>
          <a
            href="https://apps.apple.com/us/app/twism-inc/id1511105942?ls=1"
            target="_blank"
          >
            <img src={cdn('/images/app-store-badge.svg')} alt="App Store" />
          </a>
        </div>
      </div>
    {:else}
      <div class="tab tab-content-box using">
        <div class="title using-coin-tab">
          <h2>{tUsing('title')}</h2>
          <i
            class="tab-icon"
            style={`background-image: url('${process.env.BASE_URL}/images/using-coins.svg');`}
          />
        </div>
        <p>{tUsing('description')}</p>
        <Dashed class="twism-how-it-works-divider" />
        <h2>{tUsing('ways_use_coin.title')}</h2>
        <ol>
          <li>
            <p>{tUsing('ways_use_coin.step-1')}</p>
          </li>
          <li>
            <p>{tUsing('ways_use_coin.step-2')}</p>
          </li>
        </ol>
      </div>
    {/if}
  </div>
</Page>

<style lang="scss">

  :global(.twism-how-it-works-divider) {
    margin: 40px 0;
  }

  .twism-tab-wrapper {
    transition: opacity 0.3s ease-in-out;
    &.--animation {
      opacity: 0;
    }
  }

  .store-badge-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    margin-top: 32px;

    .store-badge {
      margin-right: 9px;
    }
  }

  .text-dark {
    color: $color-firefly;
  }

  h2 {
    font-weight: 600;
    font-size: 18px;
    line-height: 25.2px;
    color: var(--twism-theme-typography);
    margin-top: 0;
  }

  h3 {
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: -0.8px;
    color: var(--twism-theme-typography);
    margin-top: 8px;
    margin-bottom: 0;
  }

  p {
    font-size: 14px;
    line-height: 21px;
    color: $color-wild-blue-yonder;
  }

  ol {
    padding-left: 20px;
    margin-top: 24px;
    counter-reset: hiw-counter;

    li {
      margin-bottom: 16px;
      position: relative;
      counter-increment: hiw-counter;
      font-style: normal;
      font-weight: normal;
      font-size: 14px;
      line-height: 20px;
      color: var(--twism-theme-typography);

      &::before {
        content: counter(hiw-counter) '.';
        position: absolute;
        top: 0;
        left: -20px;
        font-style: normal;
        font-weight: 600;
        font-size: 14px;
        line-height: 20px;
      }

      &::marker {
        color: transparent !important;
        opacity: 0 !important;
      }
    }

    ul {
      padding-left: 32px;

      li::before {
        width: 4px;
        height: 4px;
        left: -10px;
      }
    }
  }

  .tab {
    padding: 32px 19px 0;
    box-sizing: content-box;
    max-height: 370px;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;

    &::-webkit-scrollbar {
      display: none;
    }

    @media screen and (max-width: 468px) {
      max-height: unset;
    }

    .title {
      h2 {
        max-width: 144px;
      }
    }

    &.using {
      .tab-icon {
        width: 56px;
        height: 56px;
        background-size: contain;
        margin-bottom: 10px;
      }
    }

    &.earning {
      .tab-icon {
        width: 60px;
        height: 40px;
        background-size: contain;
      }
    }

    .title {
      display: flex;
      flex-flow: row nowrap;
      justify-content: space-between;
      align-items: flex-start;
      margin-top: 40px;
    }

    .using-coin-tab {
      margin-top: 0;
    }

    &-icon {
      display: block;
    }
  }
</style>
